import React from "react";
import { Link } from "react-router-dom";
import { motion, useReducedMotion } from "framer-motion";
import api from "@/lib/api";

export const track = (event, meta = {}) => {
  try { api.post("/commerce/telemetry", { event, meta }).catch(() => {}); } catch (_) {}
};

// Scroll-reveal wrapper — communicates function, honors prefers-reduced-motion.
export function Reveal({ children, delay = 0, y = 24, className = "", as = "div", ...rest }) {
  const reduce = useReducedMotion();
  const M = motion[as] || motion.div;
  if (reduce) return <div className={className} {...rest}>{children}</div>;
  return (
    <M
      className={className}
      initial={{ opacity: 0, y }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, margin: "-80px" }}
      transition={{ duration: 0.7, delay, ease: [0.22, 1, 0.36, 1] }}
      {...rest}
    >
      {children}
    </M>
  );
}

export function Eyebrow({ children, tone = "coach", className = "" }) {
  const c = { coach: "text-tc-coach", violet: "text-tc-mastery", amber: "text-tc-xp" }[tone];
  return (
    <div className={`font-mono text-xs uppercase tracking-[0.22em] ${c} ${className}`}>{children}</div>
  );
}

export function Section({ children, className = "", id, ...rest }) {
  return (
    <section id={id} className={`relative w-full px-6 sm:px-8 ${className}`} {...rest}>
      <div className="mx-auto max-w-6xl">{children}</div>
    </section>
  );
}

export function SectionHeading({ children, className = "" }) {
  return (
    <h2 className={`font-heading text-2xl sm:text-3xl lg:text-[2.6rem] font-bold tracking-tight leading-[1.08] text-tc-txt ${className}`}>
      {children}
    </h2>
  );
}

export function Lede({ children, className = "" }) {
  return <p className={`text-base sm:text-lg leading-relaxed text-tc-sub ${className}`}>{children}</p>;
}

const STATE_STYLES = {
  EARLY: "text-tc-sub border-white/15",
  DEVELOPING: "text-tc-coach border-tc-coach/40",
  "NEAR TRIGGER": "text-tc-xp border-tc-xp/50",
  NEAR_TRIGGER: "text-tc-xp border-tc-xp/50",
  TRIGGERED: "text-tc-bull border-tc-bull/50",
  INVALIDATED: "text-tc-bear border-tc-bear/50",
  ACTIONABLE: "text-tc-bull border-tc-bull/50",
  CONDITIONAL: "text-tc-coach border-tc-coach/40",
  WAIT: "text-tc-xp border-tc-xp/50",
  PASS: "text-tc-pass border-tc-pass/50",
  "INSUFFICIENT EVIDENCE": "text-tc-muted border-white/15",
};

export function StatePill({ children, className = "" }) {
  const s = STATE_STYLES[children] || "text-tc-sub border-white/15";
  return (
    <span className={`inline-flex items-center gap-1.5 rounded-full border px-2.5 py-0.5 font-mono text-[10px] uppercase tracking-[0.14em] ${s} ${className}`}>
      {children}
    </span>
  );
}

const CTA_BASE =
  "inline-flex items-center justify-center gap-2 rounded-xl px-6 py-3 text-sm font-semibold transition-all focus:outline-none focus:ring-2 focus:ring-tc-coach focus:ring-offset-2 focus:ring-offset-tc-bg";

export function PrimaryCTA({ children, className = "", ...rest }) {
  return (
    <button className={`${CTA_BASE} bg-tc-coach text-tc-bg hover:opacity-90 hover:shadow-[0_0_28px_rgba(6,182,212,0.35)] ${className}`} {...rest}>
      {children}
    </button>
  );
}

export function SecondaryCTA({ children, className = "", ...rest }) {
  return (
    <button className={`${CTA_BASE} border border-white/15 text-tc-txt hover:border-tc-coach/50 hover:text-tc-coach ${className}`} {...rest}>
      {children}
    </button>
  );
}

export function Panel({ children, className = "", glow = "" }) {
  const g = { coach: "shadow-[0_0_28px_rgba(6,182,212,0.16)]", violet: "shadow-[0_0_28px_rgba(139,92,246,0.16)]", amber: "shadow-[0_0_28px_rgba(245,158,11,0.16)]" }[glow] || "";
  return (
    <div className={`rounded-2xl border border-white/10 bg-tc-card/80 backdrop-blur-sm ${g} ${className}`}>{children}</div>
  );
}

export function Disclaimer({ children, className = "" }) {
  return <p className={`font-mono text-[11px] leading-relaxed text-tc-muted ${className}`}>{children}</p>;
}

// Descriptive lateral internal links — connects related concepts without keyword spam.
export function RelatedLinks({ links, heading = "Keep exploring" }) {
  return (
    <Section className="pb-6">
      <div className="border-t border-white/8 pt-8">
        <div className="font-mono text-[11px] uppercase tracking-[0.16em] text-tc-sub">{heading}</div>
        <div className="mt-4 flex flex-wrap gap-x-7 gap-y-2.5" data-testid="related-links">
          {links.map(([label, to]) => (
            <Link
              key={to}
              to={to}
              data-testid={`related-${to.replace(/\//g, "")}`}
              className="text-sm text-tc-coach underline-offset-4 transition-colors hover:text-tc-txt hover:underline"
            >
              {label}
            </Link>
          ))}
        </div>
      </div>
    </Section>
  );
}
